import React, { useCallback, useState } from 'react';

import type { UUID } from '@aline/common';
import { link } from '@aline/routes';
import { Link as MuiLink } from '@mui/material';
import { useSnackbar } from 'notistack';
import { AlineButton } from 'src/components/generic/AlineButton';
import { Apollo } from 'src/gql/generated';
import { handleOpenInAlineTab } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import type { SavedQuestionRunReport } from '../../../chat-types';

const useStyles = makeStyles()(theme => ({
  button: {
    flexShrink: 0,
    fontSize: 12,
    minHeight: 24,
    padding: theme.spacing(0.25, 1),
  },
  link: {
    flexShrink: 0,
    fontSize: 12,
  },
}));

/**
 * "Save as report" on a finished library-wide run: keeps the run's questions
 * as a report over the same documents, with no further chat turn. Once saved
 * it becomes a link to that report, which is also in the thread's artifacts.
 * The save is recorded on the run, so `savedReport` keeps the link after a
 * reload, and the server returns the same report to a second save.
 */
export const SaveQuestionRunAsReportButton = React.memo(function SaveQuestionRunAsReportButton({
  batchId,
  messageId,
  savedReport,
}: {
  batchId: string;
  messageId: UUID;
  savedReport: SavedQuestionRunReport | null;
}) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [justSaved, setJustSaved] = useState<SavedQuestionRunReport | null>(null);
  const [saveQuestionRunAsReport, { loading }] = Apollo.useSaveQuestionRunAsReportMutation();
  const saved = justSaved ?? savedReport;

  const handleSave = useCallback(async () => {
    try {
      const { data } = await saveQuestionRunAsReport({ variables: { batchId, messageId } });
      if (data) setJustSaved(data.saveQuestionRunAsReport);
    } catch {
      enqueueSnackbar('Could not save the report. Please try again.', { variant: 'error' });
    }
  }, [batchId, enqueueSnackbar, messageId, saveQuestionRunAsReport]);

  if (saved) {
    const url = link.ReportDetail({ reportId: saved.reportId });
    return (
      <MuiLink
        className={classes.link}
        href={url}
        onClick={event => handleOpenInAlineTab(event, url, { title: saved.name })}
      >
        Open report
      </MuiLink>
    );
  }

  return (
    <AlineButton
      buttonClassName={classes.button}
      color="primary"
      loading={loading}
      size="small"
      variant="text"
      onClick={handleSave}
    >
      Save as report
    </AlineButton>
  );
});
